import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ExternalLink, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * News Carousel Component
 * Displays related news articles in a horizontal scrollable carousel
 * 
 * Features:
 * - Horizontal scrollable carousel (or vertical list)
 * - News cards:
 *   - Thumbnail
 *   - Title
 *   - Source name + date
 *   - Summary (2-3 lines, truncated)
 *   - Click → Open source URL in new tab
 */
export default function NewsCarousel({ news }) {
  const handleNewsClick = (article) => {
    if (article.source_url) {
      window.open(article.source_url, '_blank');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="w-full">
      <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
        {news.map((article) => (
          <Card
            key={article.id}
            className="min-w-[300px] max-w-[300px] cursor-pointer hover:shadow-lg transition-shadow flex-shrink-0"
            onClick={() => handleNewsClick(article)}
          >
            <CardContent className="p-0">
              <div className="w-full h-40 overflow-hidden rounded-t-lg">
                <img
                  src={getNewsThumbnail(article, 400, 300)}
                  alt={article.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    // Fallback to placeholder if image fails to load
                    e.target.src = getNewsThumbnail({ thumbnail_url: null }, 400, 300);
                  }}
                />
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-lg mb-2 line-clamp-2">
                  {article.title}
                </h3>
                {article.summary && (
                  <p className="text-sm text-muted-foreground mb-3 line-clamp-3">
                    {article.summary}
                  </p>
                )}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Calendar className="h-3 w-3" />
                    <span>{formatDate(article.published_date)}</span>
                  </div>
                  {article.source_name && (
                    <Badge variant="outline" className="text-xs">
                      {article.source_name}
                    </Badge>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full mt-3"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNewsClick(article);
                  }}
                >
                  Read More
                  <ExternalLink className="ml-2 h-3 w-3" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

